import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import "../Accounts/ledger-page.css";
import useApi from "../../hook/useApi";
import Loader from "../Loader/Loader";
import { toast } from "react-toastify";
import { formatDateFull, generateSettlementWeeks } from "../../utils/helper";
import Swal from "sweetalert2";
import { DAY_MS } from "../../utils/constant";
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
} from "../../constants/pageSizeOptions";
import SearchableSelect from "../Common/SearchableSelect";
import "./trades-layer.css";
import UpdateTradeModal from "../model/UpdateTradeModal";

const TradesLayer = ({ pageTitle = "Trades", reportType = "COMPLETED" }) => {
  const { AsyncGetApiCall, AsyncPostApiCall } = useApi();
  const [loading, setLoading] = useState(false);
  const [trades, setTrades] = useState([]);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedTrade, setSelectedTrade] = useState(null);

  // Filter Options
  const [brokers, setBrokers] = useState([]);
  const [clients, setClients] = useState([]);
  const [segments, setSegments] = useState([]);
  const [scripts, setScripts] = useState([]);

  // Active Filters
  const [filters, setFilters] = useState({
    brokerId: "",
    userId: "",
    segment: "",
    script: "",
    startDate: "",
    endDate: "",
    orderType: "",
    settlementWeek: "",
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [limitMode, setLimitMode] = useState(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);
  const settlementWeeks = useMemo(() => generateSettlementWeeks(10), []);

  // Initial Data Fetching
  useEffect(() => {
    const fetchOptions = async () => {
      const [bRes, cRes, segRes] = await Promise.all([
        AsyncGetApiCall("/broker"),
        AsyncGetApiCall("/users/my-clients"),
        AsyncGetApiCall(
          "/trading-settings/segments/list?status=active&inSelectMode=true",
        ),
      ]);
      console.log("bRes-->", bRes, "cRes-->", cRes, "segRes-->", segRes);

      if (bRes?.success) setBrokers(bRes.data || []);
      if (cRes?.success) setClients(cRes.data || []);
      if (segRes?.success) setSegments(segRes.data || []);
    };
    fetchOptions();
  }, []);

  useEffect(() => {
    const fetchScripts = async () => {
      const res = await AsyncGetApiCall(
        `/trading-settings/scripts?status=active&inSelectMode=true&segment=${filters.segment}`,
      );
      console.log("res-->", res);
      if (res?.success) setScripts(res.data || []);
    };
    fetchScripts();
  }, [filters.segment]);

  const fetchTrades = async () => {
    setLoading(true);
    const endpoint =
      reportType === "COMPLETED" ? "/orders/history" : "/orders/pending";
    const result = await AsyncPostApiCall(endpoint, filters);

    if (result?.success) {
      setTrades(result?.data || []);
      setCurrentPage(1);
    } else {
      toast.error(result?.message || "Failed to fetch trades");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTrades();
  }, [reportType]); // Refetch when switching between Trades and Pending

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === "settlementWeek") {
      if (!value) {
        setFilters((prev) => ({
          ...prev,
          settlementWeek: "",
          startDate: "",
          endDate: "",
        }));
        return;
      }

      const [sDate, eDate] = value.split("|");
      setFilters((prev) => ({
        ...prev,
        settlementWeek: value,
        startDate: sDate.split("T")[0],
        endDate: eDate.split("T")[0],
      }));
    } else if (name === "startDate" || name === "endDate") {
      if (name === "startDate") {
        if (value >= filters.endDate && filters.endDate !== "") {
          toast.error("Start date must be less than end date", { toastId: "start-date" });
          return;
        }
      }
      if (name === "endDate") {
        if (value <= filters.startDate && filters.startDate !== "") {
          toast.error("End date must be greater than start date", { toastId: "end-date" });
          return;
        }
      }
      setFilters((prev) => ({
        ...prev,
        [name]: value,
        settlementWeek: "", // Clear settlementWeek when manual date is picked
      }));
    } else {
      setFilters((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleApplyFilters = () => {
    fetchTrades();
  };

  const handleClearFilters = () => {
    const cleared = {
      brokerId: "",
      userId: "",
      segment: "",
      script: "",
      startDate: "",
      endDate: "",
      orderType: "",
      settlementWeek: "",
    };
    setFilters(cleared);
    // Directly fetch with cleared filters
    setLoading(true);
    const endpoint =
      reportType === "COMPLETED" ? "/orders/history" : "/orders/pending";
    AsyncPostApiCall(endpoint, cleared).then((res) => {
      if (res?.success) setTrades(res.data || []);
      setLoading(false);
    });
  };

  const handleDelete = async (tradeId) => {
    const isPending = reportType === "PENDING";
    if (!isPending) return;

    const { isConfirmed } = await Swal.fire({
      title: "Cancel Order?",
      text: "Are you sure you want to CANCEL this pending order? Balance will be refunded.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2352c3",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Cancel it!",
      customClass: { popup: "swal-sm" },
    });

    if (isConfirmed) {
      setLoading(true);
      const res = await AsyncPostApiCall(`/orders/cancel/${tradeId}`, {});
      if (res?.success) {
        toast.success(res.message || "Order cancelled successfully");
        fetchTrades();
      } else {
        toast.error(res?.message || "Failed to cancel order");
      }
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return trades;
    return trades.filter(
      (t) =>
        t.userId?.loginId?.toLowerCase().includes(q) ||
        t.userId?.name?.toLowerCase().includes(q) ||
        t.symbol?.toLowerCase().includes(q) ||
        t.segment?.toLowerCase().includes(q),
    );
  }, [searchTerm, trades]);

  const pageSize =
    limitMode === "all" ? filtered.length || 1 : parseInt(limitMode, 10);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const start = (safePage - 1) * pageSize;
  const pageRows = filtered.slice(start, start + pageSize);
  const total = filtered.length;
  const showingFrom = total === 0 ? 0 : start + 1;
  const showingTo = total === 0 ? 0 : Math.min(start + pageRows.length, total);

  useEffect(() => {
    setCurrentPage((p) => Math.min(p, totalPages));
  }, [totalPages]);

  const pageButtons = useMemo(() => {
    const n = totalPages;
    if (n <= 0) return [];
    const cap = Math.min(n, 5);
    const out = [];
    for (let i = 0; i < cap; i += 1) {
      let pageNum;
      if (n <= 5) pageNum = i + 1;
      else if (safePage <= 3) pageNum = i + 1;
      else if (safePage >= n - 2) pageNum = n - 4 + i;
      else pageNum = safePage - 2 + i;
      out.push(pageNum);
    }
    return out;
  }, [totalPages, safePage]);

  return (
    <>
      <div className="d-flex align-items-center gap-2 text-secondary-light mb-24">
        <Icon icon="tabler:layout-grid" className="icon text-xl" />
        <Icon icon="tabler:chevron-right" className="icon text-sm" />
        <span className="fw-semibold text-primary-light">{pageTitle}</span>
      </div>

      <div className="card border shadow-sm radius-16 mb-24 ledger-filter-card trades-filter-card">
        {loading && <Loader />}
        <div className="card-body p-24">
          <div className="row g-4">
            <div className="col-12 col-md-6 col-xl-3">
              <label className="form-label fw-bold text-primary-light mb-8 d-block">
                Broker
              </label>
              <SearchableSelect
                placeholder="All Brokers"
                value={filters.brokerId}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, brokerId: e.target.value }))
                }
                options={brokers.map((b) => ({ label: b.name, value: b._id }))}
              />
            </div>
            <div className="col-12 col-md-6 col-xl-3">
              <label className="form-label fw-bold text-primary-light mb-8 d-block">
                Client
              </label>
              <SearchableSelect
                placeholder="All Clients"
                value={filters.userId}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, userId: e.target.value }))
                }
                options={clients.map((c) => ({
                  label: `${c.name} (${c.loginId})`,
                  value: c._id,
                }))}
              />
            </div>
            <div className="col-12 col-md-6 col-xl-3">
              <label className="form-label fw-bold text-primary-light mb-8 d-block">
                Segment
              </label>
              <SearchableSelect
                placeholder="All Segments"
                value={filters.segment}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    segment: e.target.value,
                    script: "",
                  }))
                }
                options={segments.map((s) => ({
                  label: s.name,
                  value: s.name,
                }))}
              />
            </div>
            <div className="col-12 col-md-6 col-xl-3">
              <label className="form-label fw-bold text-primary-light mb-8 d-block">
                Instrument
              </label>
              <SearchableSelect
                placeholder={
                  filters.segment ? "All Scripts" : "Select Segment First"
                }
                disabled={!filters.segment}
                value={filters.script}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, script: e.target.value }))
                }
                options={scripts.map((s) => ({ label: s.name, value: s.name }))}
              />
            </div>
            <div className="col-12 col-md-6 col-xl-3">
              <label className="form-label fw-bold text-primary-light mb-8 d-block">
                Start Date
              </label>
              <input
                type="date"
                name="startDate"
                className="form-control radius-12 h-40-px"
                value={filters.startDate}
                onChange={handleFilterChange}
                min={new Date(
                  new Date().setFullYear(new Date().getFullYear() - 5)
                )
                  .toISOString()
                  .split("T")[0]}
                max={new Date(
                  new Date().setDate(new Date().getDate() + 7)
                )
                  .toISOString()
                  .split("T")[0]}
              />
            </div>
            <div className="col-12 col-md-6 col-xl-3">
              <label className="form-label fw-bold text-primary-light mb-8 d-block">
                End Date
              </label>
              <input
                type="date"
                name="endDate"
                className="form-control radius-12 h-40-px"
                value={filters.endDate}
                onChange={handleFilterChange}
                min={new Date(
                  new Date().setFullYear(new Date().getFullYear() - 5)
                )
                  .toISOString()
                  .split("T")[0]}
                max={new Date(
                  new Date().setDate(new Date().getDate() + 7)
                )
                  .toISOString()
                  .split("T")[0]}
              />
            </div>
            <div className="col-12 col-md-6 col-xl-3">
              <label className="form-label fw-bold text-primary-light mb-8 d-block">
                Type
              </label>
              <SearchableSelect
                placeholder="All Types"
                value={filters.orderType}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, orderType: e.target.value }))
                }
                options={[
                  { label: "Market", value: "MARKET" },
                  { label: "Limit", value: "LIMIT" },
                  { label: "Stop", value: "STOP" },
                ]}
              />
            </div>
            <div className="col-12 col-md-6 col-xl-3">
              <label className="form-label fw-bold text-primary-light mb-8 d-block">
                Settlement week
              </label>
              <SearchableSelect
                placeholder="Select"
                value={filters.settlementWeek}
                onChange={(e) =>
                  handleFilterChange({
                    target: { name: "settlementWeek", value: e.target.value },
                  })
                }
                options={settlementWeeks.map((week) => ({
                  label: week.label,
                  value: week.value,
                }))}
              />
            </div>
          </div>
          <div className="d-flex flex-wrap align-items-center gap-3 mt-24">
            <button
              type="button"
              className="btn btn-primary-600 radius-8 px-24 py-10"
              onClick={handleApplyFilters}
            >
              Filter Results
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary radius-8 px-24 py-10"
              onClick={handleClearFilters}
            >
              Clear Filters
            </button>
            <button
              type="button"
              className="btn btn-outline-primary radius-8 px-16 py-10 ms-auto"
              onClick={fetchTrades}
              disabled={loading}
              title="Refresh trades"
            >
              <Icon icon="tabler:refresh" className="me-1" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="card border shadow-sm radius-16 ledger-table-card">
        <div className="card-body p-24 pb-0 position-relative" style={{ zIndex: 10 }}>
          <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-between gap-3 mb-16">
            <div className="d-flex align-items-center gap-2">
              <span className="text-secondary-light">Show</span>
              <SearchableSelect
                placeholder="Show"
                value={limitMode}
                showSearch={false}
                onChange={(e) => {
                  setLimitMode(e.target.value);
                  setCurrentPage(1);
                }}
                options={PAGE_SIZE_OPTIONS.map((n) => ({
                  label: String(n),
                  value: String(n),
                }))}
              />
            </div>
            <div
              className="position-relative flex-grow-1"
              style={{ maxWidth: "400px" }}
            >
              <Icon
                icon="ion:search-outline"
                className="icon position-absolute top-50 start-0 translate-middle-y ms-12 text-secondary-light"
              />
              <input
                type="search"
                className="form-control ps-40 h-40-px radius-12"
                placeholder="Search by Client, Script, Symbol"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>
        </div>

        <div className="table-responsive px-16 pb-16">
          <table className="table sm-table mb-0">
            <thead>
              <tr className="bg-neutral-100">
                <th className="fw-semibold text-primary-light">D</th>
                <th className="fw-semibold text-primary-light">Date Time</th>
                <th className="fw-semibold text-primary-light">Client</th>
                <th className="fw-semibold text-primary-light">Master</th>
                <th className="fw-semibold text-primary-light">Segment</th>
                <th className="fw-semibold text-primary-light">Script</th>
                <th className="fw-semibold text-primary-light">Order Type</th>
                <th className="fw-semibold text-primary-light">Lot</th>
                <th className="fw-semibold text-primary-light">Qty</th>
                <th className="fw-semibold text-primary-light">Order Price</th>
                <th className="fw-semibold text-primary-light">Brokerage</th>
                {/* <th className="fw-semibold text-primary-light">S/L</th>
                <th className="fw-semibold text-primary-light">Target</th> */}
                {reportType === "PENDING" && (
                  <th className="fw-semibold text-primary-light text-center">
                    Action
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={11}
                    className="text-center py-40 text-secondary-light"
                  >
                    {loading
                      ? "Loading trades..."
                      : "No trades found for selected filters"}
                  </td>
                </tr>
              ) : (
                pageRows.map((t, index) => {
                  const isBuy = t.side === "BUY";
                  const rowClass = isBuy ? "bg-primary-50" : "bg-danger-50";
                  const sideClass = isBuy
                    ? "text-primary-600"
                    : "text-danger-600";

                  return (
                    <tr key={t._id} className={rowClass}>
                      <td className={`fw-bold ${sideClass}`}>
                        {t.side === "BUY" ? "B" : "S"}
                      </td>
                      <td className="text-secondary-light">
                        <span className="d-block text-sm">
                          {formatDateFull(t.createdAt).split(" ")[1]}
                        </span>
                        <span className="text-xs">
                          {formatDateFull(t.createdAt).split(" ")[0]}
                        </span>
                      </td>
                      <td className="text-secondary-light">
                        <span className="d-block fw-bold text-primary-light">
                          {t.userId?.loginId}
                        </span>
                        <span className="text-xs">{t.userId?.name}</span>
                      </td>
                      <td className="text-secondary-light">
                        <span className="d-block fw-bold text-primary-light">
                          {t.adminId?.name || "ADMIN"}
                        </span>
                        <span className="text-xs">{t.adminModel}</span>
                      </td>
                      <td className="text-secondary-light">
                        <span className="badge bg-neutral-200 text-secondary-light text-xxs">
                          {t.segment}
                        </span>
                      </td>
                      <td className="text-secondary-light">
                        <span className="d-block fw-bold text-primary-light">
                          {["NFO", "MCX", "CDS", "COMEX"].some((s) =>
                            (t.segment || "").toUpperCase().includes(s),
                          )
                            ? t.symbol
                            : t.script || t.symbol}
                        </span>
                        <span className="text-xs">{t.exchange}</span>
                      </td>
                      <td className="text-secondary-light">
                        <span
                          className={`badge ${t.orderType === "MARKET" ? "bg-success-focus text-success-600" : "bg-info-focus text-info-600"} text-xxs`}
                        >
                          {t.orderType}
                        </span>
                        {t.closeReason && (
                          <span
                            className="d-block text-danger fw-bold mt-1"
                            style={{ fontSize: "10px", whiteSpace: "nowrap" }}
                          >
                            {t.closeReason}
                          </span>
                        )}
                      </td>
                      <td className="text-secondary-light fw-medium">
                        {t.inputLot ||
                          (t.lotSize > 0 ? t.quantity / t.lotSize : t.lotSize)}
                      </td>
                      <td className="text-secondary-light fw-medium">
                        {t.quantity}
                      </td>
                      <td className="text-secondary-light">
                        <span className="d-block fw-bold text-primary-light">
                          ₹{t.orderPrice?.toFixed(2)}
                        </span>
                        <span className="text-xs text-secondary">
                          Net: ₹
                          {t.executionPrice?.toFixed(2) ||
                            t.orderPrice?.toFixed(2)}
                        </span>
                      </td>
                      <td className="text-secondary-light">
                        <span className="fw-bold text-danger-600">
                          ₹{t.totalBrokerage?.toFixed(2) || "0.00"}
                        </span>
                      </td>
                      {/* <td className="text-secondary-light fw-medium">
                        {t.stopLoss ? `₹${t.stopLoss.toFixed(2)}` : "-"}
                      </td> */}
                      {/* <td className="text-secondary-light fw-medium">
                        {t.targetPrice ? `₹${t.targetPrice.toFixed(2)}` : "-"}
                      </td> */}
                      {reportType === "PENDING" && (
                        <td className="text-center">
                          <button
                            className="btn btn-sm btn-outline-primary p-1 radius-4 me-1"
                            title="Edit Order"
                            onClick={() => {
                              setSelectedTrade(t);
                              setShowUpdateModal(true);
                            }}
                          >
                            <Icon icon="mdi:pencil-outline" />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger p-1 radius-4"
                            title="Cancel Order"
                            onClick={() => handleDelete(t._id)}
                          >
                            <Icon icon="mdi:close-circle-outline" />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="border-top d-flex flex-column flex-md-row align-items-center justify-content-between gap-3 p-24">
          <p className="mb-0 text-secondary-light text-sm">
            Showing{" "}
            <span className="fw-medium text-primary-light">
              {showingFrom} to {showingTo} of {total} entries
            </span>
          </p>
          {total > 0 && (
            <div className="d-flex align-items-center gap-1">
              <button
                type="button"
                className="btn btn-sm border radius-8 w-32-px h-32-px p-0 d-flex align-items-center justify-content-center"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={safePage <= 1}
              >
                <Icon icon="tabler:chevron-left" className="icon" />
              </button>
              {pageButtons.map((pageNum) => (
                <button
                  key={pageNum}
                  type="button"
                  className={`btn btn-sm border radius-8 w-32-px h-32-px p-0 d-flex align-items-center justify-content-center ${pageNum === safePage
                    ? "ledger-pagination-btn active-page-num"
                    : "bg-base text-secondary-light"
                    }`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              ))}
              {totalPages > 5 && safePage < totalPages - 2 && (
                <>
                  <span className="text-secondary-light px-1">…</span>
                  <button
                    type="button"
                    className="btn btn-sm border radius-8 w-32-px h-32-px p-0 d-flex align-items-center justify-content-center bg-base text-secondary-light"
                    onClick={() => setCurrentPage(totalPages)}
                  >
                    {totalPages}
                  </button>
                </>
              )}
              <button
                type="button"
                className="btn btn-sm border radius-8 w-32-px h-32-px p-0 d-flex align-items-center justify-content-center"
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={safePage >= totalPages}
              >
                <Icon icon="tabler:chevron-right" className="icon" />
              </button>
            </div>
          )}
        </div>
      </div>

      <UpdateTradeModal
        show={showUpdateModal}
        onHide={() => setShowUpdateModal(false)}
        trade={selectedTrade}
        onSuccess={fetchTrades}
      />
    </>
  );
};

export default TradesLayer;
